import type { AccountSession } from "@/domain/entities";

export type ProviderId = "email" | "google" | "apple" | "yandex" | "vk";
export type ArchiveKind = "auto" | "manual";

export interface ArchiveMeta {
  id: string;
  kind: ArchiveKind;
  day: string;
  createdAt: string;
  sizeBytes: number;
}

export interface Account {
  id: string;
  email: string;
  createdAt: string;
}

export interface EventsPayload {
  deviceId: string;
  platform: string;
  webdriver: boolean;
  events: { name: string; ts: string; props: Record<string, string | number | boolean> }[];
}

export interface DashboardAudience {
  total: number;
  last30: number;
  last7: number;
  today: number;
}

export interface Dashboard {
  generatedAt: string;
  devices: Record<"all" | "bots" | "humans", DashboardAudience>;
  daily: Record<"all" | "bots" | "humans", { day: string; fresh: number; returning: number }[]>;
  retention: Record<"all" | "bots" | "humans", { afterDays: number; cohort: number; returned: number }[]>;
  scenarios: { scenarioId: string; devices: number; entries: number; entries30: number; entries7: number; weeklyPerDevice: number }[];
  accounts: {
    total: number;
    byProvider: Record<string, number>;
    rows: { emailMasked: string; providers: string[]; createdAt: string; archives: number; bytes: number; lastArchiveAt: string | null }[];
  };
  clicks: {
    tabs: Record<string, number>;
    calendar: Record<string, number>;
    reportPeriods: Record<string, number>;
    reportShares: Record<string, number>;
    localesAuto: Record<string, number>;
    localesManual: Record<string, number>;
  };
}

export interface PushRegistration {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  timezone: string;
  deviceId: string;
}

export interface ReminderSpec {
  key: string;
  time: string;
  title: string;
  body: string;
  url: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
}

/** Everything the app asks the account server for; one implementation over HTTP, fakes in tests. */
export interface Api {
  providers(): Promise<ProviderId[]>;
  emailStart(email: string, locale: string): Promise<void>;
  emailVerify(email: string, code: string): Promise<AccountSession>;
  handoff(code: string): Promise<AccountSession>;
  me(token: string): Promise<Account>;
  logout(token: string): Promise<void>;
  deleteAccount(token: string): Promise<void>;
  listArchives(token: string): Promise<ArchiveMeta[]>;
  putArchive(token: string, day: string, kind: ArchiveKind, body: Uint8Array, contentHash: string): Promise<{ id: string; createdAt: string; sizeBytes: number }>;
  getArchive(token: string, id: string): Promise<Uint8Array>;
  /** Fire-and-forget usage events; `beacon` survives the page being closed. */
  sendEvents(payload: EventsPayload, token: string | undefined, beacon: boolean): Promise<void>;
  dashboard(token: string): Promise<Dashboard>;
  pushPublicKey(): Promise<string>;
  pushSubscribe(registration: PushRegistration): Promise<void>;
  pushReminders(endpoint: string, reminders: ReminderSpec[]): Promise<void>;
  pushDone(endpoint: string, key: string, day: string): Promise<void>;
  /** Parks a gzip snapshot for a few minutes behind a six-digit code. */
  transferCreate(body: Uint8Array): Promise<{ code: string; expiresAt: string }>;
  /** Fetches and consumes the parcel; a wrong or used code is a 404. */
  transferFetch(code: string): Promise<Uint8Array>;
}

/** Talks to `/api` on the same origin, so the same build serves every domain. */
export class HttpApi implements Api {
  constructor(
    private readonly base = "/api",
    private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
  ) {}

  async providers(): Promise<ProviderId[]> {
    return ((await this.json("GET", "/auth/providers")) as { providers: ProviderId[] }).providers;
  }

  async emailStart(email: string, locale: string): Promise<void> {
    await this.json("POST", "/auth/email/start", { email, locale });
  }

  async emailVerify(email: string, code: string): Promise<AccountSession> {
    const r = (await this.json("POST", "/auth/email/verify", { email, code })) as { token: string; account: Account };
    return { token: r.token, email: r.account.email };
  }

  async handoff(code: string): Promise<AccountSession> {
    const r = (await this.json("POST", "/auth/handoff", { code })) as { token: string; account: Account };
    return { token: r.token, email: r.account.email };
  }

  async me(token: string): Promise<Account> {
    return ((await this.json("GET", "/me", undefined, token)) as { account: Account }).account;
  }

  async logout(token: string): Promise<void> {
    await this.json("POST", "/auth/logout", undefined, token);
  }

  async deleteAccount(token: string): Promise<void> {
    await this.json("DELETE", "/me", undefined, token);
  }

  async listArchives(token: string): Promise<ArchiveMeta[]> {
    return (await this.json("GET", "/archives", undefined, token)) as ArchiveMeta[];
  }

  async putArchive(token: string, day: string, kind: ArchiveKind, body: Uint8Array, contentHash: string) {
    const response = await this.fetchFn(`${this.base}/archives/${day}?kind=${kind}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/gzip", "X-Content-Hash": contentHash },
      body: new Uint8Array(body),
    });
    await this.assertOk(response);
    return (await response.json()) as { id: string; createdAt: string; sizeBytes: number };
  }

  async getArchive(token: string, id: string): Promise<Uint8Array> {
    const response = await this.fetchFn(`${this.base}/archives/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    await this.assertOk(response);
    return new Uint8Array(await response.arrayBuffer());
  }

  async sendEvents(payload: EventsPayload, token: string | undefined, beacon: boolean): Promise<void> {
    const body = JSON.stringify(payload);
    // A beacon cannot carry a bearer header; the server then files the events as anonymous, which is fine.
    if (beacon && typeof navigator.sendBeacon === "function" && navigator.sendBeacon(`${this.base}/stats/events`, new Blob([body], { type: "application/json" }))) return;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    await this.fetchFn(`${this.base}/stats/events`, { method: "POST", headers, body, keepalive: true });
  }

  async dashboard(token: string): Promise<Dashboard> {
    return (await this.json("GET", "/stats/dashboard", undefined, token)) as Dashboard;
  }

  async pushPublicKey(): Promise<string> {
    return ((await this.json("GET", "/push/public-key")) as { publicKey: string }).publicKey;
  }

  async pushSubscribe(registration: PushRegistration): Promise<void> {
    await this.json("POST", "/push/subscribe", registration);
  }

  async pushReminders(endpoint: string, reminders: ReminderSpec[]): Promise<void> {
    await this.json("PUT", "/push/reminders", { endpoint, reminders });
  }

  async pushDone(endpoint: string, key: string, day: string): Promise<void> {
    await this.json("POST", "/push/done", { endpoint, key, day });
  }

  async transferCreate(body: Uint8Array): Promise<{ code: string; expiresAt: string }> {
    const response = await this.fetchFn(`${this.base}/transfer`, { method: "POST", headers: { "Content-Type": "application/gzip" }, body: new Uint8Array(body) });
    await this.assertOk(response);
    return (await response.json()) as { code: string; expiresAt: string };
  }

  async transferFetch(code: string): Promise<Uint8Array> {
    const response = await this.fetchFn(`${this.base}/transfer/${encodeURIComponent(code)}`);
    await this.assertOk(response);
    return new Uint8Array(await response.arrayBuffer());
  }

  private async json(method: string, path: string, body?: unknown, token?: string): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await this.fetchFn(`${this.base}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    await this.assertOk(response);
    return response.status === 204 ? undefined : response.json();
  }

  private async assertOk(response: Response): Promise<void> {
    if (response.ok) return;
    const code = await response
      .json()
      .then((b: { error?: string }) => b.error ?? "error")
      .catch(() => "error");
    throw new ApiError(response.status, code);
  }
}
